import React from 'react';
import styles from './SkeletonRow.module.scss';

// Vary widths per column so it looks organic, not uniform
const WIDTHS = ['60%', '45%', '70%', '50%', '40%', '55%', '35%'];

/**
 * Fallback row height, in px. Matches a single-line table row (12px td padding
 * top and bottom + one line of body text), which is what the Devices tables
 * render. Taller hosts pass their own `rowHeight`.
 */
const DEFAULT_ROW_HEIGHT = 44;

interface SkeletonRowProps {
  /** Number of columns the row should span. */
  columns: number;
  /** Stagger index for the shimmer animation delay. */
  index?: number;
  /**
   * Total height of the row, so the skeleton occupies the same space as the
   * real rows it stands in for and the table does not shift when data lands.
   * Applied as the `<tr>` height, which acts as a minimum. Defaults to
   * `DEFAULT_ROW_HEIGHT`; measure a real row to pick a value.
   */
  rowHeight?: number | string;
}

/**
 * A single skeleton placeholder row that matches the table layout.
 * Each cell renders a rounded bar with a shimmer gradient animation.
 * Rows are staggered so the shimmer cascades down the table.
 *
 * Horizontal cell padding is inherited from the host table's own `td` rule so
 * the bars line up with real cell content.
 */
export default function SkeletonRow({ columns, index = 0, rowHeight = DEFAULT_ROW_HEIGHT }: SkeletonRowProps) {
  return (
    <tr className={styles.skeletonRow} style={{ height: rowHeight }}>
      {Array.from({ length: columns }, (_, col) => (
        <td key={col}>
          <div
            className={styles.skeletonBar}
            style={{
              width: WIDTHS[col % WIDTHS.length],
              animationDelay: `${index * 80}ms`,
            }}
          />
        </td>
      ))}
    </tr>
  );
}

interface SkeletonRowsProps {
  /** Number of skeleton rows to render. */
  count: number;
  /** Number of columns per row. */
  columns: number;
  /** Total height of each row — see `SkeletonRowProps.rowHeight`. */
  rowHeight?: number | string;
}

/**
 * Renders `count` skeleton rows. Use in place of real data rows while loading.
 */
export function SkeletonRows({ count, columns, rowHeight }: SkeletonRowsProps) {
  if (count <= 0) {
    return null;
  }

  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonRow key={`skeleton-${i}`} columns={columns} index={i} rowHeight={rowHeight} />
      ))}
    </>
  );
}
